import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/digitale-helden-shared/client/components/component-icon-button.js';
import { mobile } from '@adornis/digitale-helden-shared/db/enums.js';
import { LASFile } from '@adornis/digitale-helden-shared/features/mediaverwaltung/db/LASFile.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseCarouselItemExpandingText, BaseCarouselItemTextUnderImage } from '../../db/BaseCarousel.js';
import { mergeBaseText } from './mergeBaseText.js';

@customElement('marketing-carousel-item')
export class MarketingCarouselItem extends ChemistryLitElement {
  @property({ attribute: false }) textMaxCharExtension?: BuildifyExtension;
  @property({ attribute: false }) tagExtension?: BuildifyExtension;
  @property({ attribute: false }) smallTextExtension?: BuildifyExtension;
  @property({ attribute: false }) h3Extension?: BuildifyExtension;
  @property({ attribute: false }) item?: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage;
  @property({ attribute: false }) isActive?: boolean;

  @state() private _isHovering: boolean = false;

  @state() private readonly _windowSizeController = new WindowSizeController(this);

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mouseenter', () => (this._isHovering = true));
    this.addEventListener('mouseleave', () => (this._isHovering = false));
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', () => {});
    this.removeEventListener('mouseleave', () => {});
  }

  override render() {
    const item = this.item;
    if (!item) return nothing;
    if (item instanceof BaseCarouselItemExpandingText) return this.renderExpandingTextItem(item);
    if (item instanceof BaseCarouselItemTextUnderImage) return this.renderTextUnderImageItem(item);
    return html` not supported item type `;
  }

  protected showHoverState(): boolean {
    return this._isHovering && !!this.isActive && !this._isMobile();
  }

  protected renderTextUnderImageItem(item: BaseCarouselItemTextUnderImage) {
    return html`
      <d-flex
        space=${this._isMobile() ? 'sm' : 'lg'}
        space-between
        ${css({
          padding: '24px',
          height: '100%',
        })}
      >
        <div>
          <!-- tags -->
          ${this.renderTags(item)}

          <!-- header-->
          ${this.renderHeading(item)}
        </div>
        <!-- image -->

        <d-grid
          ${css({
            width: '100%',
            aspectRatio: '1 / 1',
            position: 'relative',
          })}
        >
          ${this.showHoverState()
            ? this.textMaxCharExtension?.render(item.text, { isSubelement: true })
            : html`
                <img
                  alt=${item.imageID}
                  .src=${LASFile.getServeLink(item.imageID)}
                  ${css({
                    objectFit: 'cover',
                    aspectRatio: '1 / 1',
                    width: '100%',
                    gridRow: '1',
                    borderRadius: '8px',
                  })}
                />
              `}

          <!-- link button -->
          ${this.renderLinkButton(item)}
        </d-grid>
      </d-flex>
    `;
  }

  protected renderExpandingTextItem(item: BaseCarouselItemExpandingText) {
    const displayText =
      this.showHoverState() && item.text && item.textOnHover ? mergeBaseText(item.text, item.textOnHover) : item.text;

    return html`
      <d-flex
        space=${this._isMobile() ? 'sm' : 'lg'}
        space-between
        ${css({
          padding: '24px',
          height: '100%',
        })}
      >
        <div>
          <!-- tags -->
          ${this.renderTags(item)}

          <!-- header-->
          ${this.renderHeading(item)}

          <!-- small text -->
          <div ${css({ marginBottom: '4px' })}>
            ${this.item instanceof BaseCarouselItemExpandingText
              ? this.smallTextExtension?.render(item.smallText, { isSubelement: true })
              : nothing}
          </div>

          <!-- text -->
          ${this.textMaxCharExtension?.render(displayText, { isSubelement: true })}
        </div>

        <!-- image -->
        <div ${css({ position: 'relative' })}>
          ${this.showHoverState()
            ? nothing
            : html`
                <img
                  class="display-image"
                  .src=${LASFile.getServeLink(item.imageID ?? '')}
                  ${css({
                    objectFit: 'cover',
                    height: '100%',
                    width: '100%',
                    gridRow: '1',
                    borderRadius: '8px',
                    aspectRatio: '3 / 2',
                  })}
                />
              `}

          <!-- link button -->
          ${this.renderLinkButton(item)}
        </div>
      </d-flex>
    `;
  }

  private _isMobile() {
    return (this._windowSizeController.value?.width ?? 0) <= mobile;
  }

  protected renderTags(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage) {
    const tags = () => {
      if (this.showHoverState()) return this.tagExtension?.render(item.tagOnHover, { isSubelement: true });
      return this.tagExtension?.render(item.tag, { isSubelement: true });
    };

    return html` <div ${css({ marginBottom: '16px' })}>${tags()}</div> `;
  }

  protected renderHeading(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage) {
    return html`
      <div ${css({ marginBottom: '24px' })}>${this.h3Extension?.render(item.heading, { isSubelement: true })}</div>
    `;
  }

  protected renderLinkButton(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage) {
    return html`
      <component-icon-button
        @mouseenter=${e => {
          e.target.setAttribute('background', this.colors.tone.darkGreen);
        }}
        @mouseleave=${e => {
          e.target.setAttribute('background', this.colors.accent);
        }}
        icon="arrow-up-right"
        href=${item.link?.link ?? ''}
        ${css({
          position: 'absolute',
          right: '0',
          bottom: '0',
          display: 'unset',
          padding: '8px 0 0 8px',
          background: '#fff',
          borderRadius: '8px 0 0 0',
          zIndex: '10',
        })}
      ></component-icon-button>
    `;
  }
}
